import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Alert,
  Divider,
  Card,
  CardContent,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
  Chip,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import UndoIcon from '@mui/icons-material/Undo';
import { ReturnDialog } from '../../components/Evaluation/ReturnDialog';
import { ReturnCommentHistory } from '../../components/Evaluation/ReturnCommentHistory';
import {
  GoalReferencePanel,
  type EvaluationGoalReference,
} from '../../components/Evaluation/GoalReferencePanel';
import {
  ItemGoalReference,
  hasAnyItemGoal,
} from '../../components/Evaluation/ItemGoalReference';
import {
  canReject,
  type EvaluationStatus
} from '../../utils/statusLabels';
import { getScoreDescription, getShortScoreDescription, getScoreColor } from '../../utils/scoreDescription';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { extractErrorMessage } from '../../utils/errorHandler';

interface EmployeeInfo {
  evaluation_id?: string;
  employee_id: string;
  name: string;
  email: string;
  hire_date: string;
  job_grade: string;
  job_title: string;
  rank: string;
  department_name: string;
  department_type: string;
  status?: string;
  allow_out_of_period_edit?: boolean;
}

interface EvaluationItem {
  item_detail_id: string;
  item_id: string;
  category_number: number;
  category_name: string;
  item_number: string;
  item_description: string;
  weight: number;
  target_description: string;
  achievement_criteria: string;
  first_evaluation_score: number;
  first_evaluation_comment: string;
  second_evaluation_score?: number;
  second_evaluation_comment?: string;
}

interface EvaluationItemInput {
  second_evaluation_score: number;
  second_evaluation_comment: string;
}

interface GroupedItems {
  [category: string]: EvaluationItem[];
}

const ManagerEvaluationFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { employeeId, periodId } = useParams<{ employeeId: string; periodId: string }>();
  const { user, periodStatus } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  const [employee, setEmployee] = useState<EmployeeInfo | null>(null);
  const [evaluationItems, setEvaluationItems] = useState<EvaluationItem[]>([]);
  const [formData, setFormData] = useState<{ [key: string]: EvaluationItemInput }>({});
  const [goal, setGoal] = useState<EvaluationGoalReference | null>(null);

  // Return dialog state
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    if (employeeId && periodId) {
      fetchEvaluationData();
    }
  }, [employeeId, periodId]);

  const fetchEvaluationData = async () => {
    setLoading(true);
    setError('');

    try {
      // AuthContextから現在のユーザーIDを取得（localStorageではなくuser?.employee_id）
      const currentUserId = user?.employee_id;
      const response = await api.get(
        `/manager-evaluation/${employeeId}/${periodId}`
      );

      if (response.data.success) {
        const { employee, items, evaluatorItemScores, goal: goalData } = response.data.data;
        setEmployee(employee);
        setEvaluationItems(items);
        setGoal(goalData ?? null);

        // Initialize form data from evaluatorItemScores if exists, otherwise use defaults
        const initialFormData: { [key: string]: EvaluationItemInput } = {};

        items.forEach((item: EvaluationItem) => {
          // Find this evaluator's saved score for this item
          const savedScore = evaluatorItemScores?.find(
            (eis: any) => eis.evaluator_id === currentUserId && eis.item_id === item.item_id
          );

          initialFormData[item.item_id] = {
            second_evaluation_score: savedScore?.evaluation_score || item.second_evaluation_score || 6,
            second_evaluation_comment: savedScore?.evaluation_comment || item.second_evaluation_comment || '',
          };
        });

        setFormData(initialFormData);
        setDataLoaded(true);

        // Check if this evaluator has already submitted using isCompleted flag from backend
        if (response.data.data.isCompleted === true) {
          setSubmitted(true);
        }
      }
    } catch (error) {
      console.error('評価データ取得エラー:', error);
      setError(extractErrorMessage(error, '評価データの取得に失敗しました'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (itemId: string, field: keyof EvaluationItemInput, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: value,
      },
    }));
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const evaluation_items = evaluationItems.map((item) => ({
        item_id: item.item_id,
        second_evaluation_score: formData[item.item_id]?.second_evaluation_score,
        second_evaluation_comment: formData[item.item_id]?.second_evaluation_comment || '',
      }));

      const response = await api.post(
        '/manager-evaluation/draft',
        {
          employee_id: employeeId,
          period_id: periodId,
          evaluation_items,
        }
      );

      if (response.data.success) {
        setSuccess('上長評価を一時保存しました');
        // 一覧画面に戻る
        setTimeout(() => {
          navigate(`/manager-evaluation/${periodId}`);
        }, 1500);
      }
    } catch (error) {
      console.error('一時保存エラー:', error);
      setError(extractErrorMessage(error, '一時保存に失敗しました'));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    // Validate all items are evaluated
    const missingItems = evaluationItems.filter(
      (item) => !formData[item.item_id]?.second_evaluation_score
    );

    if (missingItems.length > 0) {
      setError('すべての項目を評価してください');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const evaluation_items = evaluationItems.map((item) => ({
        item_id: item.item_id,
        second_evaluation_score: formData[item.item_id].second_evaluation_score,
        second_evaluation_comment: formData[item.item_id].second_evaluation_comment || '',
      }));

      const response = await api.post(
        '/manager-evaluation/submit',
        {
          employee_id: employeeId,
          period_id: periodId,
          evaluation_items,
        }
      );

      if (response.data.success) {
        setSuccess('上長評価を提出しました');
        setSubmitted(true);
        setTimeout(() => {
          navigate(`/manager-evaluation/${periodId}`);
        }, 1500);
      }
    } catch (error) {
      console.error('提出エラー:', error);
      setError(extractErrorMessage(error, '提出に失敗しました'));
    } finally {
      setSaving(false);
    }
  };

  const handleReturn = async (comment: string) => {
    if (!employee?.evaluation_id) return;

    try {
      const response = await api.post(
        `/evaluations/${employee.evaluation_id}/reject`,
        { comment }
      );

      if (response.data.success) {
        setSuccess('評価を差し戻しました');
        setReturnDialogOpen(false);
        // 上長評価リストに戻る
        setTimeout(() => {
          navigate(`/manager-evaluation/${periodId}`);
        }, 1500);
      }
    } catch (error) {
      console.error('差し戻しエラー:', error);
      setError(extractErrorMessage(error, '差し戻しに失敗しました'));
    }
  };

  const canReturnEvaluation = () => {
    return employee?.status ? canReject(employee.status as EvaluationStatus, 'manager') : false;
  };

  const canEvaluate =
    !submitted &&
    ((periodStatus?.canSubmitManagerEvaluation ?? true) || employee?.allow_out_of_period_edit === true) &&
    employee?.status === 'self_evaluated';

  const groupItemsByCategory = (items: EvaluationItem[]): GroupedItems => {
    return items.reduce((acc: GroupedItems, item) => {
      const key = `${item.category_number}. ${item.category_name}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(item);
      return acc;
    }, {});
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!employee) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">評価データが見つかりません</Alert>
      </Container>
    );
  }

  const groupedItems = groupItemsByCategory(evaluationItems);
  const hasItemGoals = hasAnyItemGoal(evaluationItems);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/manager-evaluation/${periodId}`)}
          sx={{ mb: 2 }}
        >
          一覧に戻る
        </Button>
        <Typography variant="h4" component="h1" gutterBottom>
          上長評価
        </Typography>
        <Typography variant="body2" color="text.secondary">
          評価対象者の自己評価を確認し、上長評価を入力してください
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <GoalReferencePanel
        goal={goal}
        title="目標の申請状況"
        itemGoalsShown={hasItemGoals}
      />

      {/* 従業員情報 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            従業員情報
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                社員番号
              </Typography>
              <Typography variant="body1">{employee.employee_id}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                氏名
              </Typography>
              <Typography variant="body1">{employee.name}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                職級
              </Typography>
              <Typography variant="body1">{employee.job_grade}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                役職
              </Typography>
              <Typography variant="body1">{employee.job_title}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                部門
              </Typography>
              <Typography variant="body1">{employee.department_name}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                ランク
              </Typography>
              <Typography variant="body1">{employee.rank}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 評価項目 */}
      {Object.entries(groupedItems).map(([category, items]) => (
        <Accordion key={category} defaultExpanded sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">{category}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              {items.map((item, index) => (
                <Paper
                  key={item.item_id}
                  sx={{
                    p: 3,
                    mb: index < items.length - 1 ? 2 : 0,
                    backgroundColor: '#fafafa',
                  }}
                >
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      {item.item_number}. {item.item_description}
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                        sx={{ ml: 2 }}
                      >
                        (重み: {item.weight})
                      </Typography>
                    </Typography>
                  </Box>

                  <ItemGoalReference
                    targetDescription={item.target_description}
                    achievementCriteria={item.achievement_criteria}
                    title="この項目の目標（確定）"
                  />

                  <Divider sx={{ my: 2 }} />

                  {/* 自己評価（読み取り専用） */}
                  <Box sx={{ mb: 3, p: 2, backgroundColor: '#f0f0f0', borderRadius: 1 }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      自己評価
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          自己評価点
                        </Typography>
                        <Typography variant="h6" color="primary">
                          {item.first_evaluation_score || '-'} 点
                        </Typography>
                      </Grid>
                      {item.first_evaluation_comment && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            自己評価コメント
                          </Typography>
                          <Typography variant="body1">
                            {item.first_evaluation_comment}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Box>

                  {/* 上長評価（入力可能） */}
                  <Box>
                    <Typography variant="subtitle2" color="secondary" gutterBottom>
                      上長評価
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="subtitle2">上長評価点（1-10点）</Typography>
                          <Chip
                            label={`${formData[item.item_id]?.second_evaluation_score || 6}点 - ${getShortScoreDescription(formData[item.item_id]?.second_evaluation_score || 6)}`}
                            color={getScoreColor(formData[item.item_id]?.second_evaluation_score || 6)}
                            size="small"
                          />
                        </Box>
                        <Slider
                          key={`slider-${item.item_id}-${formData[item.item_id]?.second_evaluation_score || 6}`}
                          value={formData[item.item_id]?.second_evaluation_score || 6}
                          onChange={(_, value) =>
                            handleInputChange(
                              item.item_id,
                              'second_evaluation_score',
                              value as number
                            )
                          }
                          min={1}
                          max={10}
                          step={1}
                          marks={[
                            { value: 1, label: '1' },
                            { value: 6, label: '6 (基準)' },
                            { value: 10, label: '10' },
                          ]}
                          valueLabelDisplay="auto"
                          disabled={!canEvaluate}
                        />
                        {/* 評価点の詳細説明 */}
                        <Box
                          sx={{
                            mt: 1,
                            p: 2,
                            bgcolor: getScoreDescription(formData[item.item_id]?.second_evaluation_score || 6).isStandard
                              ? 'info.50'
                              : 'grey.50',
                            borderRadius: 1,
                            borderLeft: 4,
                            borderColor: getScoreDescription(formData[item.item_id]?.second_evaluation_score || 6).isStandard
                              ? 'info.main'
                              : 'grey.300',
                          }}
                        >
                          <Typography variant="caption" color="text.secondary" display="block" fontWeight="bold" mb={0.5}>
                            評価点の意味：
                          </Typography>
                          <Typography variant="body2" color="text.primary">
                            {getScoreDescription(formData[item.item_id]?.second_evaluation_score || 6).description}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          label="上長評価コメント"
                          value={formData[item.item_id]?.second_evaluation_comment || ''}
                          onChange={(e) =>
                            handleInputChange(
                              item.item_id,
                              'second_evaluation_comment',
                              e.target.value
                            )
                          }
                          placeholder="評価のコメントを入力してください&#13;&#10;例：&#13;&#10;・目標達成状況について&#13;&#10;・特に評価できる点&#13;&#10;・今後の期待や改善点"
                          helperText="自己評価を踏まえた上長としてのコメントを記入してください"
                          disabled={!canEvaluate}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Paper>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}

      {/* 差し戻し履歴セクション */}
      {employee?.evaluation_id && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              差し戻し履歴
            </Typography>
            <ReturnCommentHistory
              evaluationId={employee.evaluation_id}
              showAll={true}
            />
          </CardContent>
        </Card>
      )}

      {/* 操作ボタン */}
      <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        {canReturnEvaluation() && (
          <Button
            variant="outlined"
            color="warning"
            size="large"
            startIcon={<UndoIcon />}
            onClick={() => setReturnDialogOpen(true)}
          >
            差し戻す
          </Button>
        )}
        <Button
          variant="outlined"
          size="large"
          onClick={handleSaveDraft}
          disabled={saving || !canEvaluate}
        >
          一時保存
        </Button>
        <Button
          variant="contained"
          size="large"
          onClick={handleSubmit}
          disabled={saving || !canEvaluate}
        >
          評価を提出
        </Button>
      </Box>

      {/* Return Dialog */}
      <ReturnDialog
        open={returnDialogOpen}
        onClose={() => setReturnDialogOpen(false)}
        onSubmit={handleReturn}
        title="自己評価を差し戻す"
        targetName={employee?.name}
        maxLength={1000}
      />
    </Container>
  );
};

export default ManagerEvaluationFormPage;
